import type { ApiResponse } from '@/types';

function getApiBaseUrl(): string {
  const envUrl = import.meta.env.VITE_API_URL;
  if (!envUrl) return '/api';
  if (envUrl.endsWith('/api')) return envUrl;
  return envUrl.replace(/\/$/, '') + '/api';
}

const API_BASE_URL = getApiBaseUrl();

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('auth_token');
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_BASE_URL}${endpoint}`, { ...options, headers });
  const data: ApiResponse<T> = await response.json();
  
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Unknown error');
  }
  
  return data.data as T;
}

// ============================================
// ACTIVITY TYPES
// ============================================

export interface SkillXpGain {
  skillKey: string;
  xp: number;
}

export interface CharXpGain {
  char: string;
  amount: number;
}

export interface ChronicleEntryPreview {
  narrative: string;
}

export interface ActivityResult {
  xpGained: number;
  skillsUpdated: SkillXpGain[];
  levelUp: boolean;
  newLevel: number;
  streak: number;
  streakUpdated?: boolean;
  // Fatigue data
  fatigueBefore: number;
  fatigueAfter: number;
  fatigueReduced: number;
  // Characteristic XP gains
  xpGains: CharXpGain[];
  // Chronicle entry
  chronicleEntry?: ChronicleEntryPreview | null;
}

export interface DailyStats {
  activitiesCount: number;
  totalXp: number;
  meditationMinutes: number;
  streak: number;
}

export interface ActivityHistoryItem {
  id: string;
  activityType: string;
  activityData: Record<string, unknown>;
  xpEarned: number;
  skillsAffected: SkillXpGain[];
  createdAt: string;
}

// ============================================
// API CALLS
// ============================================

export async function logMeditation(durationMinutes: number): Promise<ActivityResult> {
  return request<ActivityResult>('/activities/meditation', {
    method: 'POST',
    body: JSON.stringify({ durationMinutes }),
  });
}

export async function logBreathing(
  durationMinutes: number, 
  technique?: string
): Promise<ActivityResult> {
  return request<ActivityResult>('/activities/breathing', {
    method: 'POST',
    body: JSON.stringify({ durationMinutes, technique }),
  });
}

export async function logReflection(
  type: 'daily' | 'weekly' | 'deep',
  content?: string
): Promise<ActivityResult> {
  return request<ActivityResult>('/activities/reflection', {
    method: 'POST',
    body: JSON.stringify({ type, content }),
  });
}

export async function getDailyStats(): Promise<DailyStats> {
  return request<DailyStats>('/activities/daily-stats');
}

export async function getActivityHistory(
  limit?: number,
  offset?: number
): Promise<{ activities: ActivityHistoryItem[] }> {
  const params = new URLSearchParams();
  if (limit) params.set('limit', String(limit));
  if (offset) params.set('offset', String(offset));

  const query = params.toString();
  return request<{ activities: ActivityHistoryItem[] }>(
    `/activities/history${query ? `?${query}` : ''}`
  );
}

// ============================================
// FATIGUE
// ============================================

export interface FatigueInfo {
  fatigue: number;
  maxFatigue: number;
  percentage: number;
  cityState: 'shining' | 'alive' | 'tired' | 'sleeping';
  cityMessage: string;
  wasReset: boolean;
}

export async function getFatigue(): Promise<FatigueInfo> {
  return request<FatigueInfo>('/fatigue');
}

// ============================================
// REFLECTION AI DIALOGUE
// ============================================

export interface ReflectionChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export async function getReflectionOpener(): Promise<string> {
  const result = await request<{ message: string }>('/reflection/opener');
  return result.message;
}

export async function sendReflectionMessage(
  message: string,
  history: ReflectionChatMessage[]
): Promise<{ reply: string; isComplete: boolean }> {
  return request<{ reply: string; isComplete: boolean }>('/reflection/message', {
    method: 'POST',
    body: JSON.stringify({ message, history }),
  });
}
